import { EditorView } from '@codemirror/view'
import { EditorSelection } from '@codemirror/state'

/**
 * Returns true if the brace at index `i` in `text` is escaped (preceded by an
 * odd number of backslashes), e.g. \{, \\\{, etc.
 */
const isEscapedBrace = (text: string, i: number) => {
  let count = 0
  for (let j = i - 1; j >= 0 && text[j] === '\\'; j--) count++
  return count % 2 === 1
}

/**
 * Find the position of the '{' that matches a '}' at index `closeIdx`.
 * Returns -1 if no match. Respects escaped braces.
 */
const findMatchingOpen = (text: string, closeIdx: number) => {
  let depth = 1
  for (let i = closeIdx - 1; i >= 0; i--) {
    const ch = text[i]
    if (ch === '}' && !isEscapedBrace(text, i)) {
      depth++
    } else if (ch === '{' && !isEscapedBrace(text, i)) {
      depth--
      if (depth === 0) return i
    }
  }
  return -1
}

/**
 * Find the position of the '}' that matches a '{' at index `openIdx`.
 * Returns -1 if no match. Respects escaped braces.
 */
const findMatchingClose = (text: string, openIdx: number) => {
  let depth = 1
  for (let i = openIdx + 1; i < text.length; i++) {
    const ch = text[i]
    if (ch === '{' && !isEscapedBrace(text, i)) {
      depth++
    } else if (ch === '}' && !isEscapedBrace(text, i)) {
      depth--
      if (depth === 0) return i
    }
  }
  return -1
}

/**
 * Jump the cursor between matching braces. Respects LaTeX-escaped braces
 * (\{ and \}).
 *
 * Behavior depends on cursor position:
 *  - If the cursor is right after a '}' (or directly on a '}'): jump back
 *    to the position right before the matching '{'.
 *  - If the cursor is directly before a '{': jump forward past the matching
 *    '}'.
 *  - Otherwise: jump forward past the closing brace of the nearest
 *    enclosing unmatched '{'.
 */
export const jumpToEnclosingCloseBrace = (view: EditorView) => {
  const { state } = view
  const pos = state.selection.main.head
  const text = state.doc.toString()

  // Case A: cursor is right after a '}' — jump back to the matching '{'.
  if (
    pos > 0 &&
    text[pos - 1] === '}' &&
    !isEscapedBrace(text, pos - 1)
  ) {
    const openPos = findMatchingOpen(text, pos - 1)
    if (openPos === -1) return false
    view.dispatch({
      selection: EditorSelection.cursor(openPos),
      scrollIntoView: true,
    })
    return true
  }

  // Case B: cursor is directly on a '}' — jump back to the matching '{'.
  if (pos < text.length && text[pos] === '}' && !isEscapedBrace(text, pos)) {
    const openPos = findMatchingOpen(text, pos)
    if (openPos === -1) return false
    view.dispatch({
      selection: EditorSelection.cursor(openPos),
      scrollIntoView: true,
    })
    return true
  }

  // Case C: cursor is directly on a '{' — jump past its matching '}'.
  let openPos = -1
  if (pos < text.length && text[pos] === '{' && !isEscapedBrace(text, pos)) {
    openPos = pos
  } else {
    // Case D: find the nearest unmatched opening brace going backwards.
    let depth = 0
    for (let i = pos - 1; i >= 0; i--) {
      const ch = text[i]
      if (ch === '}' && !isEscapedBrace(text, i)) {
        depth++
      } else if (ch === '{' && !isEscapedBrace(text, i)) {
        if (depth === 0) {
          openPos = i
          break
        }
        depth--
      }
    }
  }

  if (openPos === -1) return false

  const closePos = findMatchingClose(text, openPos)
  if (closePos === -1) return false

  view.dispatch({
    selection: EditorSelection.cursor(closePos + 1),
    scrollIntoView: true,
  })
  return true
}

export const cloneSelectionVertically =
  (forward: boolean, cumulative: boolean) => (view: EditorView) => {
    const { main, ranges, mainIndex } = view.state.selection
    const { anchor, head, goalColumn } = main
    const start = EditorSelection.range(anchor, head, goalColumn)
    const nextRange = view.moveVertically(start, forward)
    let filteredRanges = [...ranges]

    if (!cumulative && filteredRanges.length > 1) {
      // remove the current main range
      filteredRanges.splice(mainIndex, 1)
    }

    // prevent duplication when going in the opposite direction
    filteredRanges = filteredRanges.filter(
      item => item.from !== nextRange.from && item.to !== nextRange.to
    )
    const selection = EditorSelection.create(
      filteredRanges.concat(nextRange),
      filteredRanges.length
    )
    view.dispatch({ selection })
    return true
  }
